import { IProject } from '@api/models';
import { GoogleMap } from 'modules/shared/features/google-map';
import { memo, useMemo } from 'react';

interface MapProjectMarkerProps {
    project: IProject;
}

const MapProjectMarker = ({ project }: MapProjectMarkerProps) => {
    const position = useMemo(() => {
        const lat = +((project as any)?.location?.latitude ?? NaN);
        const lng = +((project as any)?.location?.longitude ?? NaN);

        return { lat, lng };
    }, [project]);

    // ✅ keep hooks before return - so compute content before conditional return
    const markerContent = useMemo(() => {
        return (
            <div className="relative flex justify-center">
                {/* Tooltip card */}
                <div className="absolute flex flex-col items-center drop-shadow-md pointer-events-none -translate-y-full mb-3">
                    <div className="bg-card-color fill-card-color px-3 py-2 rounded-lg text-xs">
                        <div className="flex items-center gap-3">
                            <div>
                                <div className="font-medium whitespace-nowrap">{project.name}</div>
                                {(project as any)?.location?.name ? (
                                    <p className="text-xs text-grayscale-500 whitespace-nowrap">
                                        {(project as any).location.name}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Base dot */}
                <div className="w-3 h-3 rounded-full bg-blue-600 border-2 border-white shadow" />
            </div>
        );
    }, [project.name, (project as any)?.location?.name]);

    // ✅ now it's safe to return null
    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return null;

    return (
        <GoogleMap.AdvancedMarker position={position} zIndex={5} title={project.name}>
            {markerContent}
        </GoogleMap.AdvancedMarker>
    );
};

export default memo(MapProjectMarker, (prev, next) => {
    const p = prev.project as any;
    const n = next.project as any;

    return (
        prev.project._id === next.project._id &&
        prev.project.name === next.project.name &&
        p?.coverImageUrl === n?.coverImageUrl &&
        p?.location?.latitude === n?.location?.latitude &&
        p?.location?.longitude === n?.location?.longitude &&
        p?.location?.name === n?.location?.name
    );
});
